import React, { useState, useEffect } from 'react';
import { Calculator, Ruler, Weight, Package, DollarSign } from 'lucide-react';
import { FbaCalculator } from '@/lib/fba-calculator';
import type { FbaCalculationInput, FbaCalculationResult, UnitType, ProductType } from '@/types';
import { formatCurrency, formatWeight, formatDimensions, cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Input from '@/components/ui/input';
import Button from '@/components/ui/button';

export default function FbaFeeCalculator() {
  const [input, setInput] = useState<FbaCalculationInput>({
    dimensions: { length: 20, width: 15, height: 10, unit: 'metric' },
    weight: { value: 500, unit: 'g' },
    price: 25,
    productType: 'standard',
    isPeakSeason: false
  });

  const [result, setResult] = useState<FbaCalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 单位标签
  const getUnitLabels = (unit: UnitType) => {
    switch (unit) {
      case 'metric':
        return { length: 'cm', weight: 'g' };
      case 'imperial':
        return { length: 'in', weight: 'lb' };
      case 'imperial-oz':
        return { length: 'in', weight: 'oz' };
    }
  };

  // 验证输入
  const validateInput = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (input.dimensions.length <= 0) {
      newErrors.length = '长度必须大于0';
    }
    if (input.dimensions.width <= 0) {
      newErrors.width = '宽度必须大于0';
    }
    if (input.dimensions.height <= 0) {
      newErrors.height = '高度必须大于0';
    }
    if (input.weight.value <= 0) {
      newErrors.weight = '重量必须大于0';
    }
    if (input.price <= 0) {
      newErrors.price = '价格必须大于0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 计算FBA费用
  const calculateFbaFee = async () => {
    if (!validateInput()) return;

    setIsCalculating(true);
    try {
      // 模拟计算延迟
      await new Promise(resolve => setTimeout(resolve, 500));
      const calculationResult = FbaCalculator.calculate(input);
      setResult(calculationResult);
    } catch (error) {
      console.error('计算错误:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // 自动计算
  useEffect(() => {
    const timer = setTimeout(() => {
      if (validateInput()) {
        const calculationResult = FbaCalculator.calculate(input);
        setResult(calculationResult);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [input]);

  // 更新输入
  const updateInput = (field: string, value: any) => {
    setInput(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateDimension = (dimension: string, value: string) => {
    // 允许空值，这样用户可以删除所有数字重新输入
    if (value === '' || value === '-') {
      setInput(prev => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [dimension]: 0 // 临时设为0，但不影响计算，因为会在验证时被拦截
        }
      }));
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setInput(prev => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [dimension]: numValue
        }
      }));
    }
  };

  const updateWeight = (value: string) => {
    // 允许空值，这样用户可以删除所有数字重新输入
    if (value === '' || value === '-') {
      setInput(prev => ({
        ...prev,
        weight: {
          ...prev.weight,
          value: 0 // 临时设为0，但不影响计算，因为会在验证时被拦截
        }
      }));
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setInput(prev => ({
        ...prev,
        weight: {
          ...prev.weight,
          value: numValue
        }
      }));
    }
  };

  const unitLabels = getUnitLabels(input.dimensions.unit);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="w-5 h-5 text-amazon-blue" />
            <span>FBA费用计算器</span>
          </CardTitle>
          <CardDescription>
            基于2026年亚马逊最新FBA费率表，支持各种产品类型和季节性费用计算
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 单位选择 */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-3 block">
              输入单位
            </label>
            <div className="flex space-x-4">
              {[
                { value: 'metric' as UnitType, label: '公制 (cm/g)' },
                { value: 'imperial' as UnitType, label: '英制 (in/lb)' },
                { value: 'imperial-oz' as UnitType, label: '英制 (in/oz)' }
              ].map((unit) => (
                <label key={unit.value} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="unit"
                    value={unit.value}
                    checked={input.dimensions.unit === unit.value}
                    onChange={(e) => {
                      const newUnit = e.target.value as UnitType;
                      updateInput('dimensions', { ...input.dimensions, unit: newUnit });
                      // 更新重量单位
                      const weightUnit = newUnit === 'imperial' ? 'lb' : newUnit === 'imperial-oz' ? 'oz' : 'g';
                      updateInput('weight', { ...input.weight, unit: weightUnit });
                    }}
                    className="w-4 h-4 text-amazon-blue focus:ring-amazon-blue/20"
                  />
                  <span className="text-sm font-medium">{unit.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 尺寸输入 */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
              <Ruler className="w-4 h-4" />
              <span>产品尺寸</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label={`长度 (${unitLabels.length})`}
                type="number"
                step="0.01"
                placeholder="请输入长度"
                value={input.dimensions.length === 0 ? '' : input.dimensions.length}
                onChange={(e) => updateDimension('length', e.target.value)}
                error={errors.length}
              />
              <Input
                label={`宽度 (${unitLabels.length})`}
                type="number"
                step="0.01"
                placeholder="请输入宽度"
                value={input.dimensions.width === 0 ? '' : input.dimensions.width}
                onChange={(e) => updateDimension('width', e.target.value)}
                error={errors.width}
              />
              <Input
                label={`高度 (${unitLabels.length})`}
                type="number"
                step="0.01"
                placeholder="请输入高度"
                value={input.dimensions.height === 0 ? '' : input.dimensions.height}
                onChange={(e) => updateDimension('height', e.target.value)}
                error={errors.height}
              />
            </div>
          </div>

          {/* 重量和价格 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                <Weight className="w-4 h-4" />
                <span>重量 ({unitLabels.weight})</span>
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="请输入重量"
                value={input.weight.value === 0 ? '' : input.weight.value}
                onChange={(e) => updateWeight(e.target.value)}
                error={errors.weight}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                <DollarSign className="w-4 h-4" />
                <span>售价 (USD)</span>
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="请输入售价"
                value={input.price}
                onChange={(e) => updateInput('price', parseFloat(e.target.value) || 0)}
                error={errors.price}
              />
            </div>
          </div>

          {/* 产品类型和季节 */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
                <Package className="w-4 h-4" />
                <span>产品类型</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { value: 'standard' as ProductType, label: '标准产品' },
                  { value: 'apparel' as ProductType, label: '服装类商品' },
                  { value: 'hazardous' as ProductType, label: '危险品' }
                ].map((type) => (
                  <label key={type.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="productType"
                      value={type.value}
                      checked={input.productType === type.value}
                      onChange={(e) => updateInput('productType', e.target.value as ProductType)}
                      className="w-4 h-4 text-amazon-blue focus:ring-amazon-blue/20"
                    />
                    <span className="text-sm font-medium">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="peakSeason"
                checked={input.isPeakSeason}
                onChange={(e) => updateInput('isPeakSeason', e.target.checked)}
                className="w-4 h-4 text-amazon-blue focus:ring-amazon-blue/20 rounded"
              />
              <label htmlFor="peakSeason" className="text-sm font-medium text-gray-700">
                旺季期间 (10月15日 - 1月14日，附加旺季费用)
              </label>
            </div>
          </div>

          <Button
            onClick={calculateFbaFee}
            loading={isCalculating}
            className="w-full"
            size="lg"
          >
            计算FBA费用
          </Button>
        </CardContent>
      </Card>

      {/* 计算结果 */}
      {result && (
        <div className="animate-fade-in space-y-6">
          {/* 费用概览 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-amazon-blue">费用概览</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-700">
                    {formatCurrency(result.totalFee)}
                  </div>
                  <div className="text-sm text-blue-600 mt-1">总FBA费用</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-xl font-semibold text-gray-700">
                    {result.sizeTier === 'small-standard' ? '小标准尺寸' :
                     result.sizeTier === 'large-standard' ? '大标准尺寸' :
                     result.sizeTier === 'small-oversize' ? '小大件' :
                     result.sizeTier === 'large-oversize' ? '大件' : '特殊超大件'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">产品分类</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-xl font-semibold text-gray-700">
                    {formatWeight(result.billingWeight.used, 'lb')}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">计费重量</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-xl font-semibold text-gray-700">
                    {result.priceTier === 'low' ? '<$10' :
                     result.priceTier === 'medium' ? '$10-$50' : '>$50'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">价格分段</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 详细信息 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 费用明细 */}
            <Card>
              <CardHeader>
                <CardTitle>费用明细</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">基础费用</span>
                  <span className="font-medium">{formatCurrency(result.baseFee)}</span>
                </div>
                {result.hazardousFee > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">危险品附加费</span>
                    <span className="font-medium text-orange-600">
                      +{formatCurrency(result.hazardousFee)}
                    </span>
                  </div>
                )}
                {result.peakSeasonFee > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">旺季附加费</span>
                    <span className="font-medium text-blue-600">
                      +{formatCurrency(result.peakSeasonFee)}
                    </span>
                  </div>
                )}
                {result.lowPriceDiscount < 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">低价产品折扣</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(result.lowPriceDiscount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 font-bold text-lg">
                  <span>总费用</span>
                  <span className="text-amazon-blue">{formatCurrency(result.totalFee)}</span>
                </div>
              </CardContent>
            </Card>

            {/* 尺寸和重量信息 */}
            <Card>
              <CardHeader>
                <CardTitle>尺寸和重量信息</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">产品尺寸</span>
                  <span className="font-medium">
                    {formatDimensions(
                      result.dimensions.longestSide,
                      result.dimensions.medianSide,
                      result.dimensions.shortestSide,
                      'in'
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">最长边</span>
                  <span className="font-medium">{result.dimensions.longestSide.toFixed(2)} in</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">长度+围长</span>
                  <span className="font-medium">{result.dimensions.lengthPlusGirth.toFixed(2)} in</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">实际重量</span>
                  <span className="font-medium">{formatWeight(result.billingWeight.actual, 'lb')}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">体积重量</span>
                  <span className="font-medium">{formatWeight(result.billingWeight.volumetric, 'lb')}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">计费重量</span>
                  <span className="font-medium text-blue-600">
                    {formatWeight(result.billingWeight.used, 'lb')}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}